import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { DataServiceService } from '../../../services/data-service.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AuthService } from '../../../services/auth.service';
import { Customers, CustomerContact, CustomerAddress } from '../../../interfaces';

@Component({
  selector: 'app-contact-management',
  templateUrl: './contact-management.component.html',
  styleUrl: './contact-management.component.scss'
})
export class ContactManagementComponent implements OnInit {

  @Input() selectedCustomer: any = {};
  @Input() isAdmin = false;
  @Input() hasAccess = false;
  @Input() customers: Customers[] = [];
  @Output() contactUpdated = new EventEmitter<void>();
  @Output() contactDeleted = new EventEmitter<void>();

  constructor(
    private dataServiceService: DataServiceService,
    private messageService: MessageService,
    private confirmService: ConfirmationService
  ) {}

  ngOnInit() {
    this.initializeComponent();
  }

  initializeComponent() {
    // Initialize contacts array if it doesn't exist
    if (!this.selectedCustomer.selectedAddressContacts) {
      this.selectedCustomer.selectedAddressContacts = [];
    }
  }

  // Method to add new contact to selected address
  addNewContact() {
    if (!this.selectedCustomer.selectedAddressContacts) {
      this.selectedCustomer.selectedAddressContacts = [];
    }
    
    this.selectedCustomer.selectedAddressContacts.push({
      contact_id: null,
      name: '',
      email: '',
      phone: ''
    });
  }

  /** True when this row is not persisted yet (safe to drop locally without API). */
  isNewContact(contact: any): boolean {
    return contact?.contact_id == null;
  }

  /** Show delete when user can remove this row: unsaved contact, or more than one row for persisted contacts. */
  canShowDeleteContact(contact: any): boolean {
    return this.isNewContact(contact) || this.getContactCount() > 0;
  }

  // Method to remove an unsaved contact from the list (no API)
  removeContact(index: number) {
    const contacts = this.selectedCustomer.selectedAddressContacts;
    if (!contacts || index < 0 || index >= contacts.length) return;
    const contact = contacts[index];
    if (this.isNewContact(contact) || contacts.length > 1) {
      contacts.splice(index, 1);
    }
  }

  /** Route delete: local removal for new contacts, confirmation + API for saved ones. */
  onDeleteContactClick(customer: Customers, address: any, contact: any, index: number) {
    if (this.isNewContact(contact)) {
      this.removeContact(index);
      return;
    }
    this.confirmDeleteContact(customer, address, contact);
  }

  // Method to confirm contact deletion
  confirmDeleteContact(customer: Customers, address: any, contact: any) {
    this.confirmService.confirm({
      message: `Are you sure you want to delete the contact "${contact.name || 'Unnamed Contact'}"? This action cannot be undone.`,
      header: 'Delete Contact',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
      rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
      accept: () => {
        this.deleteContact(customer, address, contact);
      }
    });
  }

  // Method to delete contact
  deleteContact(customer: Customers, address: any, contact: any) {
    const deleteData = {
      name: customer.name,
      status: customer.status,
      customer_type: customer.customer_type,
      delivers_to: customer.receivers?.map(r => r.customer_id) || [],
      receives_from: customer.suppliers?.map(s => s.customer_id) || [],
      delete_contact_ids: [contact.contact_id],
    };

    this.dataServiceService.updateCustomer(customer.customer_id, deleteData)
      .subscribe({
        next: (response) => {
          // Remove the contact from the local UI data immediately
          if (this.selectedCustomer.selectedAddressContacts) {
            const contactIndex = this.selectedCustomer.selectedAddressContacts.findIndex(
              (c: any) => c.contact_id === contact.contact_id
            );
            if (contactIndex > -1) {
              this.selectedCustomer.selectedAddressContacts.splice(contactIndex, 1);
            }
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Contact deleted successfully'
          });

          // Emit event to parent to refresh data
          this.contactDeleted.emit();
        },
        error: (error) => {
          console.error('Contact deletion error:', error);
          let errorMessage = 'Failed to delete contact';
          if (error.error?.detail) {
            errorMessage = error.error.detail;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
        }
      });
  }

  // Method to save contacts (used when updating customer)
  saveContacts() {
    // Get primary contact data from the first contact in the list
    const primaryContact = this.selectedCustomer.selectedAddressContacts && 
                          this.selectedCustomer.selectedAddressContacts.length > 0 
                          ? this.selectedCustomer.selectedAddressContacts[0] 
                          : null;

    const customerToUpdate = {
      name: this.selectedCustomer.name?.trim(),
      status: this.selectedCustomer.status,
      customer_type: this.selectedCustomer.customer_type,
      contact_person: primaryContact?.name?.trim() || this.selectedCustomer.contact_person?.trim() || null,
      email: primaryContact?.email?.trim() || this.selectedCustomer.email?.trim() || null,
      phone: primaryContact?.phone?.trim() || this.selectedCustomer.phone?.trim() || null,
      address: this.selectedCustomer.address?.trim() || null,
      city: this.selectedCustomer.city?.trim() || null,
      state: this.selectedCustomer.state?.trim() || null,
      zipcode: this.selectedCustomer.zipcode?.trim() || null,
      notes: this.selectedCustomer.notes?.trim() || '',
      service_hours_start: this.selectedCustomer.service_hours_start?.trim() || null,
      service_hours_end: this.selectedCustomer.service_hours_end?.trim() || null,
      delivers_to: this.selectedCustomer.delivers_to || [],
      receives_from: this.selectedCustomer.receives_from || [],
      // Add all contacts for the address
      contacts: this.selectedCustomer.selectedAddressContacts || [],
      // Specify which address is being updated
      address_id: this.selectedCustomer.selectedAddressId
    };

    this.dataServiceService.updateCustomer(this.selectedCustomer.customer_id, customerToUpdate)
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Contacts updated successfully'
          });

          // Emit event to parent to refresh data
          this.contactUpdated.emit();
        },
        error: (error) => {
          console.error('Contact update error:', error);
          let errorMessage = 'Failed to update contacts';

          if (error.error?.detail) {
            errorMessage = error.error.detail;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
        }
      });
  }

  // Helper method to validate contact data
  validateContact(contact: any): boolean {
    return contact.name && contact.name.trim().length > 0;
  }

  // Helper method to check if contacts are valid
  areContactsValid(): boolean {
    if (!this.selectedCustomer.selectedAddressContacts || this.selectedCustomer.selectedAddressContacts.length === 0) {
      return true; // No contacts is valid
    }

    return this.selectedCustomer.selectedAddressContacts.every((contact: any) => 
      !contact.name || contact.name.trim().length > 0
    );
  }

  // Method to get contact count
  getContactCount(): number {
    return this.selectedCustomer.selectedAddressContacts ? this.selectedCustomer.selectedAddressContacts.length : 0;
  }

  // Method to check if there are contacts
  hasContacts(): boolean {
    return this.getContactCount() > 0;
  }
}
